import { produce } from 'immer';
import {
  OmniElement,
  OmniStyleElement,
  html,
  css,
  repeat,
  styleMap,
  createRef,
  ref,
  unsafeCSS,
  nothing,
  classMap,
} from 'omni-ui';
import 'omni-wysiwyg';

import { Om2Dropdown } from 'omni-campaign-ui';
import './exploration-edit-card.js';
import '../campaign-image-carousel.js';

import { loadScriptFromCDN } from '../../helpers/utils.js';
import { gridstackStyles } from '../../third-party/gridstack.styles.js';

OmniStyleElement.register();
Om2Dropdown.register();

const GRID_MARGIN = 1.429;

/**
 * @description Module that allows clients to upload text and images onto draggable and resizable cards
 * @extends OmniElement
 */
export class CampaignExplorationViz extends OmniElement {
  /**
   * @summary - modules reactive properties
   * @property {Object} cards - - object with unique keys used as IDs with the value containing the data for each individual exploration card passed
   * @property {Boolean} isEditView - tracks whether to render {@link ExplorationEditCard}
   * @property {Boolean} isLegendVisible - tracks whether the legend should rendered
   * @property {Object} savedData - data received from the backend passed in by {@link CampaignStepDetail}
   * @property {Boolean} showCarousel - tracks whether to render {@link CampaignImageCarousel}
   * @property {Object} schema - passed by admin. Values for all the module's titles, labels and displayed text
   * @property {Object} context - campaign context object
   */
  static get properties() {
    return {
      cards: { type: Object },
      isLegendVisible: { type: Boolean },
      isEditView: { type: Boolean },
      savedData: { type: Object },
      showCarousel: { type: Boolean },
      readOnly: { type: Boolean, attribute: 'read-only', reflect: true },
      schema: { type: Object },
      context: { type: Object },
    };
  }

  /**
   * Exploration Viz module styles
   */
  static get styles() {
    return [
      super.styles,
      gridstackStyles,
      css`
        /* Overall parent container css, header, textarea */
        :host {
          --grid-stack-item-margin: ${unsafeCSS(GRID_MARGIN)}rem;
        }

        omni-tile::part(body-slot) {
          display: flex;
          flex-flow: column nowrap;
          height: 89.5rem;
          padding: 2.143rem 2.857rem 4.286rem 2.857rem;
        }

        .omni .title {
          color: var(--color-core-dark);
        }

        omni-tooltip:hover {
          cursor: pointer;
        }

        .omni textarea {
          overflow-y: auto;
        }

        textarea::placeholder {
          font-size: 1rem;
          color: var(--color-core-medium);
        }
        .omni textarea:focus-visible {
          outline: solid thin var(--color-primary);
        }

        /* switch and Legend */
        omni-tile[drag-able] {
          --drag-left-min: 5.6rem;
          --drag-left-max: calc(100vw - 17.5rem);
          --drag-top-min: 10.714rem;
          --drag-top-max: calc(100vh - 24rem);
          --drag-top: calc(0vh + 20rem);
          z-index: 2;
        }

        omni-tile.legend::part(body-slot) {
          position: relative;
          width: 16.429rem;
          height: 17.571rem;
          padding: 0 0 2.143rem;
          border-radius: 0.714rem;
          box-shadow:
            0 1.714rem 2.214rem -0.429rem rgba(1, 33, 78, 0.37),
            0 0.143rem 0.643rem -0.071rem rgba(1, 33, 78, 0.1);
          border: solid thin var(--color-core-medium-two);
          background-color: var(--color-core-light);
        }

        .legend-body {
          gap: 0.286rem;
        }

        .legend-icon {
          width: 1rem;
          height: 1rem;
          border-radius: 50%;
          background-color: var(--color-primary);
          margin-right: 0.857rem;
        }

        /*drag, drop, and resize grids */
        .grids-container {
          position: relative;
          height: 100%;
          margin: -0.7145rem 5.071rem;
          pointer-events: none;
        }

        .placeholder-grid {
          position: absolute;
          padding: calc(var(--grid-stack-item-margin) / 2);
          width: 100%;
          height: 100%;
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          grid-template-rows: repeat(4, 1fr);
          gap: var(--grid-stack-item-margin);
          grid-template-areas:
            't00 t01 t02 t03'
            't10 t11 t12 t13'
            't20 t21 t22 t23'
            't30 t31 t32 t33';
          justify-content: stretch;
          align-content: stretch;
          pointer-events: auto;
        }
        .grid-stack > .grid-stack-item[gs-w='4'] {
          width: 100%;
        }

        .grid-stack > .grid-stack-item[gs-w='3'] {
          width: 75%;
        }

        .grid-stack > .grid-stack-item[gs-w='2'] {
          width: 50%;
        }
        .grid-stack > .grid-stack-item[gs-w='1'] {
          width: 25%;
        }

        .grid-stack > .grid-stack-item[gs-x='3'] {
          left: 75%;
        }

        .grid-stack > .grid-stack-item[gs-x='2'] {
          left: 50%;
        }

        .grid-stack > .grid-stack-item[gs-x='1'] {
          left: 25%;
        }

        .grid-stack .grid-stack-placeholder > .placeholder-content {
          border-radius: 0.714rem;
        }

        /* grid placeholder tile */
        .placeholder {
          background-color: var(--color-pale-grey-two);
          border-radius: 0.714rem;
        }

        :host(:not([read-only])) .placeholder:hover {
          cursor: pointer;
          box-shadow:
            0 1.071rem 0.857rem -0.643rem rgba(var(--rgb-nav-two), 0.18),
            0 0.071rem 0.714rem -0.214rem rgba(var(--rgb-nav-two), 0.15);
        }

        .placeholder-btn {
          width: 100%;
        }

        .show-on-hover {
          display: none;
          font-family: OpenSans;
          text-align: center;
          color: var(--color-core-medium);
        }

        .placeholder-btn:hover .show-on-hover {
          display: block;
        }

        /* girdstack.js items */
        .grid-stack-item {
          pointer-events: auto;
          cursor: pointer;
          background-color: rgb(var(--rgb-core-light));
        }

        :host([read-only]) .grid-stack-item:hover {
          cursor: default;
        }

        .grid-stack-item:hover {
          cursor: grab;
          background-color: none;
        }

        .grid-stack-item-content {
          border-radius: 0.714rem;
          pointer-events: auto;
        }

        .grid-stack-item.ui-draggable-dragging,
        .grid-stack-item.ui-resizable-resizing {
          background-color: transparent;
          opacity: 0.5;
        }
        /* Override non configurable inline style set by gridstack */
        .omni .grid-stack-item div.ui-resizable-handle {
          z-index: 4 !important;
        }

        /* Exploration Card */
        .exploration-card {
          width: 100%;
          height: 100%;
          position: relative;
          overflow: hidden;
        }

        .exploration-title {
          color: rgb(var(--rgb-core-light));
          z-index: 1;
        }

        .exploration-card:hover .exploration-title {
          opacity: 0.15;
        }

        .exploration-actions {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          margin: 1.429rem 1.714rem;
          display: none;
          z-index: 3;
        }
        .exploration-card:hover .exploration-actions {
          display: block;
        }

        .exploration-actions .button-container {
          gap: 0.5rem;
        }

        omni-icon[icon-id='omni:interactive:view']::part(icon) {
          margin-top: 0.21rem;
        }
        /* necessary in order to fight omni-ui's implementation of --color-icon-lines */
        .exploration-actions .is-text omni-icon {
          fill: rgb(var(--rgb-core-light)) !important;
          --color-icon-lines: rgb(var(--rgb-core-light));
        }

        .exploration-actions .is-text:hover omni-icon {
          fill: rgb(var(--rgb-core-dark)) !important;
          --color-icon-lines: rgb(var(--rgb-core-dark));
        }

        .exploration-notes {
          z-index: 1;
          width: 100%;
          height: 100%;
        }

        .exploration-notes omni-wysiwyg {
          color: rgb(var(--rgb-core-light));
        }

        .top-row {
          top: 0;
          bottom: auto;
        }

        .far-right {
          right: 0;
          left: auto;
        }
      `,
    ];
  }

  /**
   * @summary Initializes modules default properties
   * @property {Object} cardActions - Object containing function, icon and tooltip for exploration card menu/action buttons
   * @property {String} cardID- id passed into {@link _setEditView} to determine which exploration card is being created/updated
   * @property {Object} cards - object with unique keys used as IDs with the value containing the data for each individual exploration card passed
   * @property {Object} explorationTypes - object containing the Type for each exploration card determining the cards opacity
   * @property {Object} gridRef - Object containing reference to the GridStack DOM node
   * @property {Number} idCounter - Starting id number used to as the starting id for the keys of {@link cards}
   * @property {Boolean} isEditView - tracks whether to render {@link ExplorationEditCard}
   * @property {Boolean} isLegendVisible - tracks whether the legend should rendered
   * @property {String} moduleTitle - title of Module
   * @property {String} moduleTooltip - tooltip explaining the goal of the module
   * @property {Boolean} showCarousel - tracks whether to render {@link CampaignImageCarousel}
   * @property {String} textAreaContent - tracks the content added to the module main text area
   * @property {String} tileOnHoverText - text to show when you hover a placeholder
   * @property {Boolean} readOnly - attribute reflected which is set from context so that we can conditionally style elements that need to be disabled on readOnly
   */
  constructor() {
    super();
    this.cardActions = {
      edit: {
        icon: 'omni:interactive:edit',
        callback: id => this._setEditView(true, id),
        tooltip: 'Edit',
        order: 0,
      },
      delete: {
        icon: 'omni:interactive:delete',
        callback: id => this._removeCard(id),
        tooltip: 'Remove',
        order: 1,
      },
      view: {
        icon: 'omni:interactive:view',
        callback: id => this._toggleCarousel(id),
        tooltip: 'View',
        order: 2,
      },
    };
    this.cardID = '';
    this.cards = {};
    this.gridRef = createRef();
    this.idCounter = 40;
    this.isEditView = false;
    this.isLegendVisible = false;
    this.moduleTitle = '';
    this.moduleSubtitle = '';
    this.moduleTooltip = '';
    this.showCarousel = false;
    this.textAreaLabel = '';
    this.textareaContent = '';
    this.tileOnHoverText = '';
    this.readOnly = false;
  }

  /**
   * @summary Assigns module properties to the passed in values found in {@link savedData} or defaults them to the values {@link exploration-default-data }.
   * {@link cards} object is reduced to assign an internal id identifier needed to keep track of the data.
   */
  connectedCallback() {
    super.connectedCallback();
    const cards = this.savedData?.explorationData?.cards ?? [];
    const textAreaContent =
      this.savedData?.explorationData?.textAreaContent ?? '';
    const {
      moduleTitle,
      moduleSubtitle,
      moduleTooltip,
      textAreaLabel,
      tileOnHoverText,
      explorationTypes,
      coreIssue,
    } = this.schema;
    this.cards = cards.reduce((agg, crr) => {
      // eslint-disable-next-line no-param-reassign
      agg[this.idCounter] = crr;
      this.idCounter += 10;
      return agg;
    }, {});
    this.moduleTitle = moduleTitle;
    this.moduleSubtitle = moduleSubtitle;
    this.moduleTooltip = moduleTooltip;
    this.tileOnHoverText = tileOnHoverText;
    this.textAreaLabel = textAreaLabel;
    this.textAreaContent = textAreaContent;
    this.explorationTypes = explorationTypes;
    this.displayCoreIssue = coreIssue;
    this.readOnly = this.context.readonly;
  }

  /**
   * Instantiates a GridStack instance and sets all the parameters
   */
  firstUpdated() {
    this._setupGridStack();
  }

  /**
   * Subscribing to context change so that internal readOnly property triggers lifecycle
   * @todo Revisit this -JRH
   */
  updated(changedProperties) {
    if (changedProperties.has('context')) {
      this.readOnly = this.context.readonly;
    }
  }

  /**
   * Initialize the grid and it's listeners after loading the gridstack script
   * from the campaign CDN.
   */
  _setupGridStack = async () => {
    await loadScriptFromCDN(
      'gridstack@7.1.0/dist/gridstack-all.js',
      'GridStack',
    );
    // eslint-disable-next-line no-undef
    this.grid = GridStack.init(
      {
        column: 4,
        row: 4,
        float: true,
        disableOneColumnMode: true,
        cellHeight: '25%',
        margin: `${GRID_MARGIN / 2}rem`,
        resizable: { handles: 'n,e,s,w,se,sw' },
        staticGrid: this.readOnly,
      },
      this.gridRef.value,
    );
    this.grid.on('change', this._onChange);
  };

  /**
   *
   * @param {*} _e - Custom event
   * @param {Array} list - items that changed
   * @summary - invoked by {@link grid} on change event. Updates each card in {@link cards} with the data that shares its id and invokes {@link _assetChange}
   */

  _onChange = (_e, list) => {
    this.cards = produce(this.cards, draft => {
      list.forEach(card => {
        const id = card.el?.id?.split('-')[1];
        const { h, w, x, y } = card;
        draft[id] = { ...draft[id], h, w, x, y };
      });
    });
    this._assetChange();
  };

  /**
   * @summary dispatches an event @event omni-module-changed with the id of the module that changed and the function to call on saveAction
   */
  _assetChange() {
    const { uuid_module: moduleId } = this.context;
    this.dispatchNewEvent('omni-module-changed', {
      detail: {
        moduleId,
        saveAction: this._submitAttachments.bind(this),
        moduleType: 'exploration viz',
      },
    });
  }

  /**
   * @summary Submits and saves the data to CampaignAPI under the correct campaign and module.
   * We remove the unique internal key/id of each individual card and return an array of objects from explorationData.cards.
   */
  async _submitAttachments() {
    this.explorationData = {
      textAreaContent: this.textAreaContent,
      cards: Object.values(this.cards),
    };
    const sharedData = { '/studio/coreIssue': this.textAreaContent };
    await this.save({ explorationData: this.explorationData }, sharedData);
  }

  /**
   *
   * @summary tracks whether to render {@link ExplorationEditCard}, and if a user is updating or creating a card
   * @param {Boolean} update - boolean whether we are updating a card or not
   * @param {String} id - id of card being updated or created
   */
  _setEditView = (update, id) => {
    this.isEditView = !this.isEditView;
    this.cardID = id;
    this.shouldUpdateCard = update;
  };

  /**
   *
   * @summary - toggles {@link CampaignImageCarousel} and sets {@link cardID} to the id of the card that toggled {@link CampaignImageCarousel}
   * @param {String} id - id of the card that is toggling the carousel
   */
  _toggleCarousel = id => {
    this.cardID = id;
    this.showCarousel = !this.showCarousel;
  };

  /**
   *
   * @param {String} id - id of card or placeholder that invoked {@link _renderEditCard}
   * @returns - {@link ExplorationEditCard} html element
   */
  _renderEditCard = id => {
    let gridArea;
    if (id in this.cards) {
      gridArea = `${this.cards[id].y}${this.cards[id].x}`;
    }
    return html`
      <exploration-edit-card
        style="grid-area:t${gridArea || id}; z-index:5"
        class=${classMap({
          'top-row': (gridArea || id).startsWith('0'),
          'far-right': (gridArea || id).endsWith('3'),
        })}
        .explorationCard=${this.cards[id] || {}}
        .gridCoordinates=${{ x: id?.split('')[1], y: id?.split('')[0] }}
        .env=${this.env}
        .context=${this.context}
        .explorationTypes=${Object.keys(this.explorationTypes)}
        .assistTemplateContext=${this._assistTemplateContext}
        @edit-card-close=${() => this._setEditView(false, null)}
        @edit-card-submit=${this.shouldUpdateCard
          ? e => this._updateCard(e.detail)
          : e => this._createCard(e.detail)}
      ></exploration-edit-card>
    `;
  };

  /**
   *
   * @summary - updates existing card in {@link cards} array
   * @param {Object} card - data passed in by @event edit-card-submit event detail
   */
  _updateCard = card => {
    this.cards = produce(this.cards, draft => {
      draft[this.cardID] = card;
    });
    const wysiwygNode = this.shadowRoot.querySelector(
      `omni-wysiwyg[data-card-id="${this.cardID}"]`,
    );
    wysiwygNode.setContent(this.cards[this.cardID].notes);
    this._setEditView(false, null);
    this._assetChange();
  };

  /**
   *
   * @summary - creates a new card in {@link cards} array and makes the newly rendered card into a Gridstack widget.
   * @param {Object} card -data passed in by @event edit-card-submit event detail
   */
  _createCard = async card => {
    this.cards = produce(this.cards, draft => {
      draft[this.idCounter] = card;
    });
    await this.updateComplete;
    const el = this.shadowRoot.querySelector(`#card-${this.idCounter}`);
    this.grid.makeWidget(el);
    this.idCounter += 10;
    this._setEditView(false, null);
    this._assetChange();
  };

  /**
   *
   * @summary - removes card both from {@link cards} array and GridStack's widgets array
   * @param {String} id - id of the card to remove
   */
  _removeCard = id => {
    const el = this.shadowRoot.querySelector(`#card-${id}`);
    this.grid.removeWidget(el, false, false);
    this.cards = produce(this.cards, draft => {
      delete draft[id];
    });
    this._assetChange();
  };

  _assistTemplateContext = templates =>
    templates.map(template => {
      if (template === 'observations') {
        // Flatten all wysiwyg data structures to just text for assist prompt
        const text = Array.from(
          this.shadowRoot.querySelectorAll('omni-wysiwyg'),
        )
          .map(node => node.editor.getText())
          .join('\n')
          .trim();
        if (text !== '') return text;
      }
      return undefined;
    });

  /**
   *
   * @returns exploration card that displays the data found in {@link cards} array and acts as the gridstack widget
   */
  _renderCards = () =>
    repeat(
      Object.keys(this.cards),
      id => id,
      id => html`
        <div
          id=${`card-${id}`}
          class="grid-stack-item"
          gs-w=${this.cards[id].w}
          gs-h=${this.cards[id].h}
          gs-x=${this.cards[id].x}
          gs-y=${this.cards[id].y}
        >
          <div class="grid-stack-item-content">
            <div
              class="exploration-card py-5 px-5 is-flex is-flex-direction-column is-flex-wrap-nowrap"
              style="background: rgba(var(--rgb-primary),${this
                .explorationTypes[this.cards[id].type]?.value || 1})"
            >
              <div
                class="exploration-title is-size-2 has-text-weight-semibold mb-4"
              >
                ${this.cards[id]?.title}
              </div>
              <div class="exploration-actions">
                ${this._renderCardActions(id)}
              </div>
              <div class="exploration-notes">
                <omni-wysiwyg
                  data-card-id="${id}"
                  .initialContent=${this.cards[id].notes}
                  display-only
                  overflow-visible
                >
                </omni-wysiwyg>
              </div>
            </div>
          </div>
        </div>
      `,
    );

  /**
   *
   * @param {String} id - id belonging to the parent container exploration card
   * @returns - exploration card action buttons
   */
  _renderCardActions = id => html`
    <div class="button-container is-flex is-justify-content-end">
      ${repeat(
        Object.keys(this.cardActions).sort(
          (a, b) => this.cardActions[a].order - this.cardActions[b].order,
        ),
        btn => btn,
        btnKey => {
          if (this.readOnly && ['edit', 'delete'].includes(btnKey))
            return nothing;
          const btn = this.cardActions[btnKey];
          return html`
            <omni-tooltip>
              <button
                class="button is-text"
                slot="invoker"
                @click=${() => btn.callback(id)}
              >
                <omni-icon icon-id=${btn.icon} class="is-size-2"></omni-icon>
              </button>
              <div slot="content">${btn.tooltip}</div>
            </omni-tooltip>
          `;
        },
      )}
    </div>
  `;

  /**
   *
   * @returns html component. Legend that shows what color corresponds to what exploration type
   */
  _renderLegend() {
    const typeKeys = Object.keys(this.explorationTypes);
    return html` <omni-style>
      <omni-tile drag-able ?hidden=${!this.isLegendVisible} class="legend">
        <div
          class="py-4 px-3 is-flex is-flex-wrap-warp is-justify-content-space-between is-align-items-center"
        >
          <h3 class="mb-0 title is-4 has-text-weight-normal">Legend</h3>
          <button
            class=" button is-shadowless is-text"
            @click="${() => {
              this.isLegendVisible = false;
            }}"
          >
            <omni-icon
              class="is-size-3"
              icon-id="omni:interactive:minus"
            ></omni-icon>
          </button>
        </div>
        <div class="legend-body is-flex is-flex-direction-column">
          ${typeKeys.map(
            type => html`
              <div class="ml-5 is-flex is-align-items-center  ">
                <div
                  class="legend-icon"
                  style=${styleMap({
                    opacity: this.explorationTypes[type].value,
                  })}
                ></div>
                ${this.explorationTypes[type].label}
              </div>
            `,
          )}
        </div>
      </omni-tile>
    </omni-style>`;
  }

  /**
   *
   * @returns placeholders that create a 4x4 grid
   */
  _renderPlaceholder = () =>
    new Array(16).fill().map((_, index) => {
      const row = Math.floor(index / 4);
      const column = index % 4;
      const gridArea = `${row}${column}`;
      return html`
        <div
          class="placeholder is-flex is-flex-wrap-wrap is-align-content-stretch"
          style="grid-area:t${gridArea}"
        >
          ${this.readOnly
            ? nothing
            : html`
                <div
                  role="button"
                  tabindex="0"
                  class="placeholder-btn p-4 is-flex is-flex-wrap-wrap is-align-content-center is-justify-content-center"
                  @click=${() => this._setEditView(false, gridArea)}
                  @keydown=${e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.target.click();
                    }
                  }}
                >
                  <p class="show-on-hover">${this.tileOnHoverText}</p>
                </div>
              `}
        </div>
      `;
    });

  /**
   *
   * @returns {TemplateResult} main HTML to be rendered on the DOM
   */
  render() {
    return html`
      <omni-style>
        <omni-tile>
          <div class="mb-6 ">
            <h2 class="title">${this.moduleTitle}</h2>
            <p class="subtitle is-size-4">${this.moduleSubtitle}</p>
          </div>
          <div class="mb-6">
            <omni-switch
              .checked=${this.isLegendVisible}
              @change=${e => {
                this.isLegendVisible = e.detail.checked;
              }}
            >
              <span>View Legend</span>
            </omni-switch>
            ${this._renderLegend()}
          </div>
          <div class="grids-container">
            <div class="placeholder-grid">
              ${this._renderPlaceholder()}
              ${this.isEditView ? this._renderEditCard(this.cardID) : nothing}
            </div>
            <div ${ref(this.gridRef)} class="grid-stack">
              ${this._renderCards()}
            </div>
          </div>
          ${this.displayCoreIssue
            ? html` <div class="is-flex is-flex-direction-column mt-6">
                <label class="label is-size-7" for="textarea">
                  ${this.textAreaLabel}
                  <omni-tooltip>
                    <omni-icon
                      slot="invoker"
                      class="is-size-2"
                      icon-id="omni:informative:info"
                    ></omni-icon>
                    <p slot="content">${this.moduleTooltip}</p>
                  </omni-tooltip>
                </label>
                <campaign-assist-field
                  fieldId="coreIssue"
                  .fields=${this.context.assistFields}
                  .templateContext=${this._assistTemplateContext}
                >
                  <textarea
                    ?disabled=${this.readOnly}
                    spellcheck
                    class="textarea px-5 py-3"
                    id="textarea"
                    placeholder="Enter text"
                    @input=${e => {
                      this.textAreaContent = e.target.value;
                      this._assetChange();
                    }}
                    .value=${this.textAreaContent.trim()}
                  ></textarea>
                </campaign-assist-field>
              </div>`
            : nothing}
        </omni-tile>
        ${this.showCarousel
          ? html`
              <campaign-image-carousel
                .carouselDescription=${{
                  content: this.cards[this.cardID]?.notes,
                  title: this.cards[this.cardID]?.title,
                }}
                .handleToggle=${this._toggleCarousel}
                .showCarousel=${this.showCarousel}
                .images=${this.cards[this.cardID]?.images}
              >
              </campaign-image-carousel>
            `
          : nothing}
      </omni-style>
    `;
  }
}

OmniElement.register('campaign-exploration-viz', CampaignExplorationViz);
